import { useEffect, useState } from 'react';
import { onUpdateAvailable, dismissVersion, startVersionCheck } from '../utils/versionCheck';

/**
 * Slim top banner that surfaces when a new lounge build is live.
 *
 * - Polls `/version.json` every 60s via versionCheck.ts.
 * - First appearance per new version. Once the user dismisses, the
 *   banner stays hidden for THAT version; a future deploy reshows it.
 * - Refresh button hard-reloads so the browser fetches a fresh
 *   index.html (which is no-cache via .htaccess + meta tags) → fresh
 *   bundle.
 * - Works in iOS app webview the same as in mobile Safari.
 *
 * Styling is self-contained via inline styles + a single class hook
 * (`mw-update-banner`) so it never depends on the modern-lounge CSS
 * scope.
 */
export const UpdateAvailableBanner = () => {
  const [pendingVersion, setPendingVersion] = useState<string | null>(null);

  useEffect(() => {
    startVersionCheck();
    const unsubscribe = onUpdateAvailable((v) => setPendingVersion(v));
    return unsubscribe;
  }, []);

  if (!pendingVersion) return null;

  const handleRefresh = () => {
    // Cache-bust the navigation itself so we don't get a soft reload
    // off a still-cached index.html in any intermediate proxy.
    window.location.reload();
  };

  const handleDismiss = () => {
    dismissVersion(pendingVersion);
    setPendingVersion(null);
  };

  return (
    <div
      className="mw-update-banner"
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        top: 'calc(env(safe-area-inset-top, 0) + 8px)',
        left: '12px',
        right: '12px',
        zIndex: 10020,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '10px 12px 10px 14px',
        background:
          'linear-gradient(135deg, rgba(148, 176, 227, 0.98) 0%, rgba(255, 77, 141, 0.98) 100%)',
        color: '#fff',
        borderRadius: '14px',
        boxShadow:
          '0 14px 30px rgba(7, 10, 26, 0.22), 0 1px 2px rgba(7, 10, 26, 0.08)',
        fontFamily:
          '"Space Grotesk", "HK Grotesk", Inter, system-ui, -apple-system, sans-serif',
        fontSize: '13.5px',
        fontWeight: 600,
        letterSpacing: '0.005em',
        lineHeight: 1.2,
      }}
    >
      <span aria-hidden="true" style={{ fontSize: '16px' }}>✨</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        A new Lounge update is available
      </span>
      <button
        type="button"
        onClick={handleRefresh}
        style={{
          appearance: 'none',
          border: 'none',
          background: 'rgba(255, 255, 255, 0.94)',
          color: '#0b1028',
          padding: '7px 14px',
          borderRadius: '999px',
          fontWeight: 700,
          fontSize: '12.5px',
          letterSpacing: '0.01em',
          cursor: 'pointer',
          WebkitTapHighlightColor: 'transparent',
          touchAction: 'manipulation',
        }}
      >
        Refresh
      </button>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss update notice"
        style={{
          appearance: 'none',
          border: 'none',
          background: 'transparent',
          color: 'rgba(255, 255, 255, 0.92)',
          padding: '6px 4px 6px 2px',
          fontSize: '18px',
          lineHeight: 1,
          cursor: 'pointer',
          WebkitTapHighlightColor: 'transparent',
          touchAction: 'manipulation',
        }}
      >
        ×
      </button>
    </div>
  );
};

export default UpdateAvailableBanner;
